import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import DetailsFilterButton from './DetailsFilterButton';

import styles from './DetailsRow.module.scss';

interface DetailsRowProps {
    label: string;
    /** A request-data path rather than a field name: shown as code. */
    isPath?: boolean;
    /** Null when the entry has nothing here. */
    value: string | null;
    /** The stored form when the value shows it differently, such as a date's ISO text. */
    title?: string | null;
    /** A muted aside after the value, such as a scope's id. */
    note?: string | null;
    /** The chip label when the filter's chip is not named like the row. */
    filterLabel?: string | null;
    /** Leave out for a value no filter can match. */
    onFilter?: () => void;
}

/** One `dt`/`dd` pair of a details list, ending in the value's filter button. */
export default function DetailsRow(props: DetailsRowProps) {
    const {
        label,
        isPath = false,
        value,
        title = null,
        note = null,
        filterLabel = null,
        onFilter,
    } = props;
    const { t } = useTranslation();

    return (
        <div className={styles.row}>
            <dt className={classNames(styles.label, { [styles.path]: isPath })}>{label}</dt>
            <dd className={styles.value}>
                <span className={styles.text} title={title ?? undefined}>
                    {value ?? t('auditLog.table.noValue')}
                </span>
                {note === null ? null : <span className={styles.note}>{note}</span>}
                {value === null || onFilter === undefined ? null : (
                    <span className={styles.action}>
                        <DetailsFilterButton
                            name={t('auditLog.details.filterValue', {
                                label: filterLabel ?? label,
                                value,
                            })}
                            onFilter={onFilter}
                        />
                    </span>
                )}
            </dd>
        </div>
    );
}
